import React from "react";
import type { SingleValue } from "react-select";
import t from "../../../../../i18n";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { ApiValue } from "../../Forms/formHelpers";
import { getDefaultValue, multipleValue } from "../../Forms/formHelpers";
import { uniq } from "diagnostics-lib/array";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../../../api/useCategories";
import type { ClassificationFailure } from "../../../../../api/useClassificationFailures";

// System (unique with full option)
export const useSystemInput = (selectedFailures: ClassificationFailure[]) => {
  const [placeholder, setPlaceholder] = React.useState(
    t("setup.paragraphs.quantities.table.definitions.column.system"),
  );
  const systems = useCategories("SYSTEM");
  const createSystem = useCreateCategory("SYSTEM");
  const updateSystem = useUpdateCategory("SYSTEM");
  const deleteSystem = useDeleteCategory("SYSTEM");
  const [isDirty, setIsDirty] = React.useState(false);

  const systemIds = uniq(
    selectedFailures
      .map(f => f.systemId)
      .map(c => ({
        id: c ?? "",
        name: c ?? "",
      })),
  );
  const defaultValue = getDefaultValue<ApiValue>(systemIds, "id", system => ({
    label: system.name,
    value: String(system.id),
  }));

  const [defaultSystemOption, setDefaultSystemOption] = React.useState(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) {
      return null;
    }
    return defaultValue;
  });

  const systemOptions = [
    { id: emptyOption.value, name: emptyOption.label },
    ...systems.map(c => ({ id: c, name: c })),
  ];

  const [selectedSystems, setSelectedSystems] =
    React.useState<SingleValue<BaseOption>>(defaultSystemOption);

  const handleSystemChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedSystems(option);
  }, []);

  React.useEffect(() => {
    if (selectedSystems?.value === defaultSystemOption?.value) {
      setIsDirty(false);
    } else {
      setIsDirty(true);
    }
  }, [selectedSystems, defaultSystemOption]);

  const resetValue = React.useCallback(() => {
    setSelectedSystems(defaultSystemOption);
    setIsDirty(false);
  }, [defaultSystemOption]);

  const handleSystemCreated = React.useCallback(
    async (newSystem: string) => {
      await createSystem(newSystem);
      setIsDirty(true);

      setSelectedSystems({
        label: newSystem,
        value: newSystem,
      });
    },
    [createSystem],
  );

  const handleSystemEdited = React.useCallback(
    async (option: BaseOption, label: string) => {
      if (!option.value) return;
      await updateSystem(option.value, label);
      if (selectedSystems?.value === option.value) {
        setSelectedSystems({ ...option, label });
      }
    },
    [updateSystem, setSelectedSystems, selectedSystems],
  );

  const handleSystemDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (!option.value) return;
      await deleteSystem(option.value);
      if (selectedSystems?.value === option.value) {
        setDefaultSystemOption(null);
        setSelectedSystems(null);
      }
    },
    [deleteSystem, selectedSystems],
  );

  React.useEffect(() => {
    if (defaultValue?.value === multipleValue.value)
      setPlaceholder(t("administration.modal.multiple_value"));
    else setPlaceholder(t("form.no_value"));
  }, [defaultValue]);

  return {
    placeholder,
    systemOptions,
    selectedSystems,
    setSelectedSystems,
    handleSystemChanged,
    handleSystemCreated,
    handleSystemEdited,
    handleSystemDeleted,
    isDirty,
    resetValue,
  };
};
